import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined';
import CompanyInfoCard from './CompanyInfoCard';
import {
  ApplyAdminCompanyReferralCreditAPI,
  ConfirmAdminCompanyReferralAPI,
  MarkAdminCompanyReferralRewardedAPI,
} from '../../../../api/AdminAPI';
import { referralStatus, formatReferralDate } from '../../../referral/referralStatus';

const companyLink = (id, name) => (
  <Link component={RouterLink} to={`/admin/companies/${id}`} underline="hover">
    {name || `Empresa ${id}`}
  </Link>
);

/** Rótulos da trilha (company_audit_log, ações referral.*) */
const EVENT_LABELS = {
  'referral.indicate': 'Indicação direta',
  'referral.signed_up': 'Conta criada pela indicação',
  'referral.rejected': 'Indicação recusada',
  'referral.prospect_signed_up': 'Contato indicado criou conta',
  'referral.confirmed': 'Indicação confirmada pela equipe',
  'referral.subscribed': 'Indicada assinou',
  'referral.canceled': 'Indicada cancelou a assinatura',
  'referral.reward_marked': 'Crédito marcado como aplicado',
  'referral.reward_unmarked': 'Crédito desmarcado',
  'referral.reward_applied': 'Crédito lançado no Stripe',
  'referral.reward_failed': 'Stripe recusou o crédito',
  'referral.reward_expired': 'Crédito venceu sem quem indicou assinar',
};

const CREDIT_SOURCE = {
  referred_paid: 'automático, na assinatura',
  referrer_paid: 'automático, quando quem indicou assinou',
  invoice_paid: 'automático, numa fatura paga',
  admin: 'pelo Admin Geral',
};

const formatCents = (cents) => `R$ ${(cents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const INDICATE_RESULT = {
  created: 'contato criado',
  already_known: 'número já conhecido',
  limit: 'limite do dia',
};

/**
 * Uma linha da trilha, vista da empresa aberta: quando ela é a indicadora o alvo é
 * quem ela indicou; quando ela é a indicada, o evento mostra quem a indicou.
 */
function eventText(event, companyId) {
  const label = EVENT_LABELS[event.action] || event.action;
  const details = event.details || {};
  const parts = [label];
  if (event.action === 'referral.indicate') {
    parts.push([details.name, details.whatsapp_masked].filter(Boolean).join(' '));
    if (INDICATE_RESULT[details.result]) parts.push(INDICATE_RESULT[details.result]);
  } else if (event.company_id === companyId && event.target_type === 'company' && event.target_id) {
    parts.push(`conta ${event.target_id}`);
  } else if (event.company_id !== companyId) {
    parts.push(`indicada por ${event.company_id}`);
  }
  if (details.reason === 'same_phone') parts.push('mesmo WhatsApp');
  if (details.via === 'link' && details.code) parts.push(`link ${details.code}`);
  if (details.amount_cents) parts.push(formatCents(details.amount_cents));
  if (CREDIT_SOURCE[details.source]) parts.push(CREDIT_SOURCE[details.source]);
  return parts.filter(Boolean).join(' · ');
}

/**
 * CompanyReferralCard - Indique e ganhe na empresa do Admin Geral: quem indicou esta
 * conta (confirmação da indicação direta e o crédito, que entra sozinho no Stripe e
 * pode ser aplicado na hora), quem ela
 * indicou e os últimos eventos da trilha.
 * @param {Object} props
 * @param {number} props.companyId
 * @param {Object|null} props.referral saída de ReferralService::forCompanyAdmin
 * @param {Function} props.onToast (message, severity)
 * @param {Function} props.onChanged recarrega a empresa
 */
function CompanyReferralCard({ companyId, referral, onToast, onChanged }) {
  const [saving, setSaving] = useState(false);

  if (!referral) return null;

  const referredBy = referral.referred_by;
  const pending = referral.pending_prospect;
  const counts = referral.counts || {};
  const referrals = referral.referrals || [];
  const events = referral.events || [];
  const rewarded = !!referral.rewarded_at;
  const subscribed = !!referral.subscribed;
  const credit = referral.credit || {};

  const finish = (successMessage) => (response) => {
    setSaving(false);
    if (!response || response.success === false) {
      onToast(response?.error || 'Não foi possível salvar. Tente novamente.', 'error');
      return;
    }
    onToast(successMessage, 'success');
    onChanged();
  };

  const toggleRewarded = () => {
    setSaving(true);
    if (rewarded) {
      MarkAdminCompanyReferralRewardedAPI(companyId, false, finish('Crédito desmarcado. Ele só volta pelo botão "Aplicar crédito agora".'));
      return;
    }
    ApplyAdminCompanyReferralCreditAPI(companyId, finish('Crédito lançado no Stripe de quem indicou.'));
  };

  const confirmReferral = () => {
    setSaving(true);
    ConfirmAdminCompanyReferralAPI(companyId, finish('Indicação confirmada. A conta agora conta para quem indicou.'));
  };

  let creditValue = 'Pendente';
  if (rewarded) {
    creditValue = `Aplicado em ${formatReferralDate(referral.rewarded_at)}${credit.amount_cents ? ` (${formatCents(credit.amount_cents)})` : ''}`;
  } else if (!subscribed) creditValue = 'Aguardando a primeira fatura paga';
  else if (credit.state === 'expired') creditValue = 'Venceu: quem indicou não assinou a tempo';
  else if (credit.state === 'waiting' && credit.until) creditValue = `Guardado até ${formatReferralDate(credit.until)}, à espera de quem indicou assinar`;

  const rows = [
    { label: 'Código', value: referral.code || 'Ainda não gerado' },
    {
      label: 'Indicada por',
      value: referredBy
        ? <>{companyLink(referredBy.id, referredBy.name)}{referredBy.same_phone ? ' (mesmo WhatsApp)' : ''}</>
        : (pending ? <>{companyLink(pending.referrer.id, pending.referrer.name)} (a confirmar)</> : 'Ninguém'),
    },
    referredBy && { label: 'Assinou', value: subscribed ? 'Sim' : 'Ainda não' },
    referredBy && { label: 'Crédito', value: creditValue },
    { label: 'Indicações', value: referrals.length },
    (counts.subscribed || counts.rewarded) ? { label: 'Assinaram', value: (counts.subscribed || 0) + (counts.rewarded || 0) } : null,
  ].filter(Boolean);

  const rewardBlocked = !rewarded && !subscribed;
  const rewardButton = (
    <span>
      <Button size="small" variant={rewarded ? 'text' : 'outlined'} onClick={toggleRewarded} disabled={saving || rewardBlocked}>
        {rewarded ? 'Desmarcar crédito' : 'Aplicar crédito agora'}
      </Button>
    </span>
  );

  const footer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {pending && !referredBy && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            Esta conta veio de um contato indicado na Prospecção. Confirme depois de falar com a pessoa.
            {pending.same_phone ? ' Atenção: mesmo WhatsApp de quem indicou; a confirmação será recusada.' : ''}
          </Typography>
          <Button size="small" variant="outlined" onClick={confirmReferral} disabled={saving || pending.same_phone} sx={{ alignSelf: 'flex-start' }}>
            Confirmar indicação
          </Button>
        </Box>
      )}
      {referredBy && (
        rewardBlocked
          ? <Tooltip title="O crédito só vale depois da primeira fatura paga desta conta."><Box sx={{ alignSelf: 'flex-start' }}>{rewardButton}</Box></Tooltip>
          : <Box sx={{ alignSelf: 'flex-start' }}>{rewardButton}</Box>
      )}
      {referrals.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {referrals.slice(0, 8).map((item) => (
            <Typography key={`${item.kind}-${item.id}`} variant="caption" color="text.secondary">
              {item.kind === 'company' ? companyLink(item.id, item.name) : item.name}
              {' · '}{referralStatus(item.status).label}
              {item.date ? ` · ${formatReferralDate(item.date)}` : ''}
            </Typography>
          ))}
          {referrals.length > 8 && (
            <Typography variant="caption" color="text.secondary">e mais {referrals.length - 8}</Typography>
          )}
        </Box>
      )}
      {events.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, pt: 1, borderTop: '1px solid rgba(26, 37, 89, 0.06)' }}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>Trilha</Typography>
          {events.map((event) => (
            <Typography key={event.id} variant="caption" color="text.secondary">
              {formatReferralDate(event.date)} · {eventText(event, companyId)}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );

  return (
    <CompanyInfoCard
      icon={<CardGiftcardOutlinedIcon fontSize="small" />}
      title="Indique e ganhe"
      rows={rows}
      footer={footer}
    />
  );
}

export default CompanyReferralCard;
